/**
 * Purpose-bound context, and the governed rules it is assembled under.
 *
 * Two things the registry holds apart, and this screen keeps apart too. A
 * **contract** is the stable identity of a context: what it is for, and who
 * answers for it. A **version** is everything that decides an assembly — which
 * providers may be called, what may never be assembled at all, how much history
 * must be reconstructable — and it stops changing the moment it exists, so that
 * an assembly citing it means the same thing forever. Deprecating a version
 * later does not change what a citation meant; that is why the lifecycle sits
 * outside the digest.
 *
 * Reading both is one authority. `GET /context/contracts` and a contract's
 * versions are guarded as `context.contract.read`, so this screen asks the
 * platform once and shows what it answers — the destination is offered on
 * `context_contract.read` and the route still decides on arrival.
 *
 * Registering a contract, declaring a version and moving a lifecycle are acts
 * this screen does not offer. Not greyed out: absent. The first two have no
 * screen that explains what writing a governance rule commits the tenant to,
 * and `context.contract_version.promote` is seeded to nobody by the baseline —
 * deciding a contract may be assembled against is a different job from writing
 * one, exactly as it is for brains.
 */

import { useCallback, useState } from "react";
import { api } from "../api/client";
import type { ContextContract, ContextContractVersion, ContextLifecycle, Page } from "../api/types";
import { usePagedCollection } from "../api/useCollection";
import { Card, Identifier, Mono, More, Pill, type Tone } from "../components/primitives";
import { Empty, Show } from "../components/states";

const LIFECYCLE_TONE: Record<ContextLifecycle, Tone> = {
  draft: "quiet",
  active: "ok",
  deprecated: "wait",
  retired: "quiet",
};

/** One page of the register at a time — the size this screen asks for. */
const PAGE = 50;

function ContractVersions({ contract }: { contract: ContextContract }) {
  /**
   * The paging is the shared primitive's; the path and the reset are this
   * screen's business. `resetKey` is the contract the pages belong to, so
   * choosing another contract starts at its first page rather than showing one
   * contract's versions under another's heading.
   *
   * The primitive reads pages as `PageOf`, which is narrower than the `Page`
   * the API returns: it does not carry `projection`. Nothing is discarded by
   * that here — `/context/contracts/{key}/versions` answers from
   * `ContextContractRegistry.versions`, which returns `Page.of(...)`; `Page.of`
   * never sets `projection`, and the only code in the platform that ever
   * constructs a `PageProjection` is the outbox service, which does not serve
   * this route.
   */
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<ContextContractVersion>>(`/context/contracts/${contract.key}/versions`, {
        params: { limit: PAGE, cursor },
        signal,
      }),
    [contract.key],
  );
  const versions = usePagedCollection<ContextContractVersion>(readPage, {
    resetKey: contract.key,
  });

  return (
    <Card
      title={
        <span>
          {contract.display_name}
          <span className="nx-hint" style={{ display: "block", fontWeight: 400 }}>
            <Mono>{contract.key}</Mono> · {contract.purpose} · {contract.owner}
          </span>
        </span>
      }
      bodyless
    >
      <Show
        resource={versions.state}
        what="this contract's versions"
        onRetry={versions.refresh}
        empty={() =>
          versions.items.length === 0 ? (
            <Empty
              title="No version declared."
              hint="The contract exists and nothing can be assembled under it yet: an assembly cites a version exactly, and there is none to cite."
            />
          ) : null
        }
      >
        {() => (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <caption className="nx-visually-hidden">
                Versions of {contract.display_name}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Version</th>
                  <th scope="col">Lifecycle</th>
                  <th scope="col">Subject</th>
                  <th scope="col">Temporal</th>
                  <th scope="col">Providers</th>
                  <th scope="col">Digest</th>
                </tr>
              </thead>
              <tbody>
                {versions.items.map((version) => (
                  <tr key={version.context_contract_version_id}>
                    <td className="nx-table__primary">{version.version}</td>
                    <td>
                      <Pill tone={LIFECYCLE_TONE[version.lifecycle]}>{version.lifecycle}</Pill>
                    </td>
                    <td>{version.primary_subject_type.replace(/_/g, " ")}</td>
                    <td>
                      {version.temporal_requirement.replace(/_/g, " ")}
                      {version.requires_reconstructability ? (
                        <span className="nx-hint" style={{ display: "block" }}>
                          proof required
                        </span>
                      ) : null}
                    </td>
                    {/*
                      A plain list, whole. The providers travel on the version
                      itself — a contract version is one immutable object, not a
                      collection read a page at a time — so these are all of
                      them and no bounded-disclosure sentence belongs here.
                    */}
                    <td>
                      {version.providers.map((provider) => (
                        <span key={provider.provider_key} style={{ display: "block" }}>
                          <Mono>{provider.provider_key}</Mono>{" "}
                          <span className="nx-hint">{provider.dependency}</span>
                        </span>
                      ))}
                    </td>
                    <td>
                      <Identifier value={version.digest} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Show>
      <More
        collection={versions}
        label="Show earlier versions"
        hint="This is one page of the contract's versions, newest first — not its whole history."
        placement="bodyless"
      />
    </Card>
  );
}

export function ContextContracts() {
  const [selected, setSelected] = useState<ContextContract | null>(null);

  /**
   * The same narrowing proof as the versions panel: `/context/contracts`
   * answers from `ContextContractRegistry.contracts`, which returns
   * `Page.of(...)`, so `projection` is null on every page this screen reads.
   */
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<ContextContract>>("/context/contracts", {
        params: { limit: PAGE, cursor },
        signal,
      }),
    [],
  );
  const contracts = usePagedCollection<ContextContract>(readPage);

  return (
    <div className="nx-split">
      <Card title="Context contracts" bodyless>
        <Show
          resource={contracts.state}
          what="the context contracts"
          onRetry={contracts.refresh}
          empty={() =>
            contracts.items.length === 0 ? (
              <Empty
                title="No context contract is registered."
                hint="A contract is agreed before anything assembles context under it; a tenant that has agreed none has nothing here."
              />
            ) : null
          }
        >
          {() => (
            <div className="nx-table-wrap">
              <table className="nx-table">
                <caption className="nx-visually-hidden">
                  Purpose-bound contexts this tenant has agreed exist
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Contract</th>
                    <th scope="col">Purpose</th>
                    <th scope="col">Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.items.map((contract) => (
                    <tr
                      key={contract.context_contract_id}
                      aria-selected={selected?.key === contract.key}
                    >
                      <td>
                        <button
                          type="button"
                          className="nx-rowbutton"
                          onClick={() => {
                            setSelected(contract);
                          }}
                        >
                          <span className="nx-table__primary">{contract.display_name}</span>
                          <span className="nx-table__secondary" style={{ display: "block" }}>
                            {contract.key}
                          </span>
                        </button>
                      </td>
                      <td>{contract.purpose}</td>
                      <td>{contract.owner}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Show>
        <More
          collection={contracts}
          label="Show more contracts"
          hint="This is one page of the register, not every context this tenant has agreed exists."
          placement="bodyless"
        />
      </Card>

      {selected ? (
        <ContractVersions contract={selected} />
      ) : (
        <Card title="Select a contract">
          <p className="nx-hint">
            Choose a contract to see the versions it may be assembled under: which providers each
            calls, what it refuses to assemble, and where each version is in its governed life.
          </p>
        </Card>
      )}
    </div>
  );
}
